import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plane, Globe, RefreshCw, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_COLORS = {
  eligible: "text-nb-cyan bg-nb-cyan/10 border-nb-cyan/30",
  withdraw_requested: "text-[#EAB308] bg-[#EAB308]/10 border-[#EAB308]/30",
  paid: "text-[#8AE234] bg-[#8AE234]/10 border-[#8AE234]/30",
  disabled: "text-red-400 bg-red-500/10 border-red-500/30",
};

export default function AdminTourFunds() {
  const [funds, setFunds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const url = filter === "all" ? "/admin/tour-funds" : `/admin/tour-funds?status_filter=${filter}`;
      const { data } = await api.get(url);
      setFunds(data.funds || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filter]);

  const toggle = async (fund, allow) => {
    if (!window.confirm(`${allow ? "Enable" : "Disable"} withdrawal for ${fund.user_email}? They ${allow ? "will" : "won't"} be able to submit a payout request.`)) return;
    try {
      await api.post("/admin/tour-fund/toggle", { fund_id: fund.id, allow });
      toast.success(allow ? "Withdrawal enabled — user notified" : "Withdrawal disabled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateAmount = async (fund) => {
    const raw = window.prompt(`New amount for ${fund.user_email} (${fund.kind}):`, String(fund.amount));
    if (raw == null) return;
    const amount = parseFloat(raw);
    if (isNaN(amount) || amount < 0) { toast.error("Invalid amount"); return; }
    try {
      await api.post("/admin/tour-fund/update-amount", { fund_id: fund.id, amount });
      toast.success("Amount updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tour-funds/sync-eligibility");
      toast.success(data.message);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const stats = {
    total: funds.length,
    pending: funds.filter((f) => f.status === "eligible" && !f.withdraw_enabled).length,
    ready: funds.filter((f) => f.status === "eligible" && f.withdraw_enabled).length,
    requested: funds.filter((f) => f.status === "withdraw_requested").length,
    paid: funds.filter((f) => f.status === "paid").length,
  };

  return (
    <AppLayout variant="admin" requireAdmin>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white">Tour Funds</h1>
          <div className="text-xs text-slate-500 mt-1">Manage Domestic + International tour reward payouts (auto-detected eligibility).</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="tour-fund-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="nb-input py-2 text-sm">
            <option value="all">All statuses</option>
            <option value="eligible">Eligible</option>
            <option value="withdraw_requested">Withdrawal requested</option>
            <option value="paid">Paid</option>
          </select>
          <button data-testid="tour-fund-sync-btn" disabled={busy} onClick={syncNow} className="nb-btn-secondary text-sm"><RefreshCw size={12} className="inline mr-1"/>{busy ? "Syncing..." : "Sync eligibility"}</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="tour-fund-stats">
        <StatBox label="Total" value={stats.total} color="#F8FAFC"/>
        <StatBox label="Awaiting Admin" value={stats.pending} color="#EAB308"/>
        <StatBox label="Ready" value={stats.ready} color="#00E5FF"/>
        <StatBox label="Requested" value={stats.requested} color="#F59E0B"/>
        <StatBox label="Paid" value={stats.paid} color="#8AE234"/>
      </div>

      <div className="nb-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#070B14] text-xs uppercase text-slate-500 tracking-widest">
            <tr>
              <th className="text-left px-3 py-3">User</th>
              <th className="text-left px-3 py-3">Kind</th>
              <th className="text-right px-3 py-3">Amount</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-center px-3 py-3">Withdraw Enabled</th>
              <th className="text-left px-3 py-3">Eligible at</th>
              <th className="px-3 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {funds.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500">No tour funds yet — hit "Sync eligibility" to scan users.</td></tr>}
            {funds.map((f) => {
              const badge = STATUS_COLORS[f.status] || "text-slate-400 bg-white/5 border-white/10";
              return (
                <tr key={f.id} data-testid={`tour-fund-row-${f.id}`} className="hover:bg-white/5">
                  <td className="px-3 py-3">
                    <Link to={`/admin/users/${f.user_id}`} className="hover:text-nb-cyan">
                      <div className="text-white text-sm">{f.user_name || "—"}</div>
                      <div className="text-xs text-slate-500">{f.user_email}</div>
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-xs uppercase tracking-widest flex items-center gap-1">
                    {f.kind === "domestic" ? <Plane size={12} className="text-[#8AE234]"/> : <Globe size={12} className="text-nb-cyan"/>}
                    {f.kind}
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-white">${Number(f.amount||0).toFixed(2)}</td>
                  <td className="px-3 py-3"><span className={`px-2 py-1 rounded text-xs uppercase border ${badge}`}>{f.status.replace("_"," ")}</span></td>
                  <td className="px-3 py-3 text-center">
                    <button
                      data-testid={`toggle-${f.id}`}
                      onClick={() => toggle(f, !f.withdraw_enabled)}
                      disabled={f.status === "paid"}
                      className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest border ${f.withdraw_enabled ? "border-[#8AE234] bg-[#8AE234]/10 text-[#8AE234]" : "border-slate-600 bg-slate-800/40 text-slate-400"} disabled:opacity-50`}
                    >{f.withdraw_enabled ? "ON" : "OFF"}</button>
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-400">{f.eligible_at ? new Date(f.eligible_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <button data-testid={`edit-amount-${f.id}`} onClick={() => updateAmount(f)} disabled={f.status === "paid" || f.status === "withdraw_requested"} className="text-xs px-2 py-1 rounded border border-nb-cyan/40 text-nb-cyan hover:bg-nb-cyan/10 disabled:opacity-40">Edit amount</button>
                      {f.withdrawal_id && (
                        <Link data-testid={`view-wd-${f.id}`} to="/admin/withdrawals" className="text-xs px-2 py-1 rounded border border-white/10 text-slate-300 hover:text-white"><ExternalLink size={10} className="inline"/> Withdrawal</Link>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="nb-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono text-2xl font-bold mt-1" style={{color}}>{value}</div>
    </div>
  );
}
